import { useQuery } from "@apollo/client";
import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  List,
  ListItem,
  Typography,
} from "@mui/material";
import { ReactElement, useState } from "react";

import {
  DoNotContactRequestAction,
  ListDoNotContactRequestHistoryForPhoneNumberQuery,
} from "~frontend/__generated__/graphql";
import { AsyncWrapper } from "~frontend/components/AsyncWrapper";
import { FlexBox } from "~frontend/components/FlexBox";
import { TwoColumnDisplay } from "~frontend/components/TwoColumnDisplay";
import ChevronDown from "~frontend/components/icons/ChevronDown";
import { LIST_DO_NOT_CONTACT_REQUEST_HISTORY_FOR_PHONE_NUMBER } from "~frontend/graphql/doNotContactRequests";
import { palette } from "~frontend/theme";

import { StaffRequest } from ".";
import { AddContactModal } from "./AddContactModal";
import { RemoveContactModal } from "./RemoveContactModal";

export function ContactDetailsModal({
  onClose,
  selectedRequest: selectedRequest,
}: {
  onClose: () => void;
  selectedRequest?: StaffRequest;
}): ReactElement {
  const [showRemoveContactModal, setShowRemoveContactModal] = useState(false);
  const [showAddContactModal, setShowAddContactModal] = useState(false);

  const { loading, error, data } =
    useQuery<ListDoNotContactRequestHistoryForPhoneNumberQuery>(
      LIST_DO_NOT_CONTACT_REQUEST_HISTORY_FOR_PHONE_NUMBER,
      {
        skip: !selectedRequest?.phoneNumber,
        fetchPolicy: "cache-and-network",
        variables: {
          input: {
            phoneNumber: selectedRequest?.phoneNumber,
          },
        },
      }
    );

  return (
    <Dialog open={!!selectedRequest} onClose={onClose} maxWidth="sm" fullWidth>
      <AsyncWrapper loading={[loading]} errors={[error]} data={data}>
        {({ listDoNotContactRequestHistoryForPhoneNumber }) => {
          const latestRequest = listDoNotContactRequestHistoryForPhoneNumber[0];
          const onList =
            latestRequest?.action === DoNotContactRequestAction.Add;

          return (
            <>
              <DialogTitle>
                Contact Details for{" "}
                <Box sx={{ fontWeight: 700 }} component={"span"}>
                  {selectedRequest?.phoneNumber}
                </Box>
              </DialogTitle>
              <DialogContent>
                <Box
                  sx={{
                    backgroundColor: palette.lightestGray,
                    py: 0,
                    px: 2,
                    mb: 3,
                  }}
                >
                  <TwoColumnDisplay
                    showTopBorder={false}
                    showBottomBorder={false}
                    labelVariant="body2Bold"
                    contentVariant="body2"
                    allRowSx={{ height: "45px" }}
                    rows={[
                      {
                        key: "name",
                        label: "Name",
                        content: selectedRequest?.contactName,
                      },
                      {
                        key: "email",
                        label: "Email",
                        content: selectedRequest?.contactEmail,
                      },
                    ]}
                  ></TwoColumnDisplay>
                </Box>
                <Accordion
                  disableGutters
                  sx={{ padding: 0, background: "none" }}
                >
                  <AccordionSummary
                    expandIcon={<ChevronDown sx={{ fontSize: "1em" }} />}
                    sx={{
                      backgroundColor: palette.midGray,
                      minHeight: "27px",
                      ".MuiAccordionSummary-content": { margin: 0 },
                    }}
                  >
                    <Typography variant="body2Bold">History</Typography>
                  </AccordionSummary>
                  <AccordionDetails
                    sx={{
                      p: 0,
                    }}
                  >
                    <List sx={{ backgroundColor: palette.lightestGray, px: 2 }}>
                      {listDoNotContactRequestHistoryForPhoneNumber.map(
                        request => (
                          <ListItem
                            divider
                            disableGutters
                            key={request.id}
                            sx={{
                              borderColor: palette.lightGray,

                              "&:last-child": {
                                borderBottom: "none",
                              },
                            }}
                          >
                            <FlexBox direction="column">
                              <Typography variant="body2">
                                {`${
                                  request.action ===
                                  DoNotContactRequestAction.Add
                                    ? "Added by"
                                    : "Removed by"
                                } ${request.createdByStaffEmail ? ` ${request.createdByStaffEmail}` : "staff member"}
                              `}
                              </Typography>
                              {request.notes && (
                                <Typography variant="body3">
                                  {`Notes: ${request.notes}`}
                                </Typography>
                              )}
                              <Typography variant="body3">
                                {new Date(request.createdAt).toLocaleString()}
                              </Typography>
                            </FlexBox>
                          </ListItem>
                        )
                      )}
                    </List>
                  </AccordionDetails>
                </Accordion>
              </DialogContent>
              <DialogActions sx={{ px: 3, py: 2 }}>
                <FlexBox justifyContent="space-between" sx={{ width: "100%" }}>
                  <Button
                    color={onList ? "error" : "primary"}
                    variant="outlined"
                    onClick={() => {
                      if (onList) {
                        setShowRemoveContactModal(true);
                      } else {
                        setShowAddContactModal(true);
                      }
                    }}
                  >
                    {`${onList ? "Remove from" : "Add to"} do not contact list`}
                  </Button>
                  <Button
                    color="containedTertiary"
                    variant="contained"
                    onClick={onClose}
                  >
                    Close
                  </Button>
                </FlexBox>
              </DialogActions>
              <AddContactModal
                open={showAddContactModal}
                onClose={() => setShowAddContactModal(false)}
                parentOnClose={onClose}
                phoneNumber={selectedRequest?.phoneNumber}
                contactName={selectedRequest?.contactName}
                contactEmail={selectedRequest?.contactEmail}
              />
              <RemoveContactModal
                phoneNumber={selectedRequest!.phoneNumber}
                open={showRemoveContactModal}
                parentOnClose={onClose}
                onClose={() => setShowRemoveContactModal(false)}
              />
            </>
          );
        }}
      </AsyncWrapper>
    </Dialog>
  );
}
